/**
 * A release's metadata as a label/value list.
 *
 * Used by the review step (§15) and by the detail page (§22), so the two cannot disagree
 * about what a release says. A `<dl>` because that is what this is — pairs of terms and
 * definitions — and it gives assistive technology the association for free.
 *
 * EVERY VALUE IS PRINTED THROUGH lib/publisherFormat OR SHOWN AS "—". No field is
 * back-filled with a plausible default, and in particular nothing invents a release date,
 * an ISRC or a UPC. §24/§26.
 */
import type { ReactNode } from 'react';
import { NO_DATE } from '../../lib/publisherFormat';
import { c } from '../../styles/theme';

export interface SummaryRow {
  label: string;
  /** A string, or markup where the value needs one (a badge, mono text, a list). */
  value: ReactNode;
  /** True for provider-assigned values, which get the mono face. */
  mono?: boolean;
}

export interface ReleaseSummaryProps {
  rows: readonly SummaryRow[];
}

export default function ReleaseSummary({ rows }: ReleaseSummaryProps) {
  return (
    <dl className="lyx-release-summary" style={{ margin: 0 }}>
      {rows.map((row) => (
        // A fragment per pair so the grid's own auto-placement puts dt and dd in the two
        // columns. Wrapping each pair in a div would break the single-column collapse.
        <div key={row.label} style={{ display: 'contents' }}>
          <dt style={{ fontSize: 11.5, color: c.text2, margin: 0 }}>{row.label}</dt>
          <dd
            style={{
              fontSize: 12.5,
              color: c.text1,
              margin: 0,
              minWidth: 0,
              lineHeight: 1.5,
              fontFamily: row.mono ? 'IBM Plex Mono' : undefined,
              // Long values — a genre list, a lyric excerpt, an outlet list — wrap rather
              // than widening the page.
              overflowWrap: 'anywhere',
            }}
          >
            {row.value === '' || row.value === null || row.value === undefined ? (
              // The same marker publisherFormat uses for an absent date, so "nothing
              // here" looks the same wherever it appears.
              <span style={{ color: c.text2 }}>{NO_DATE}</span>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
